import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpPlanDetailService from "@plannel/services/fp/fp-plan-detail-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, { buildFlatReportStatusBar } from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) — PlanResult/DispatchList 파일럿에서 확인된 값(9)을
// 그대로 재사용한다(fp-report-screen-guide.md §A-3c/§B-1, 이 화면도 같은 결정을 따름).
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 중 stageCdNm/inventoryCdNm은 화면에서 제외
// (2026-08-12 사용자 지시) — PlanDetailQueryRepository는 여전히 두 파라미터를 contains 조건으로
// 소비하지만, 프론트 입력 필드 자체는 노출하지 않는다.
const SEARCH_FIELD_LABELS = {
  customerCdNm: "FP_CUSTOMER", resourceCdNm: "FP_RESOURCE", routeCdNm: "FP_ROUTE",
  itemCdNm: "FP_ITEM", dmndCdNm: "FP_DEMAND", woCdNm: "FP_ORDER_CD"
};
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

const PlanDetail = ({ t, viewName, title }) => {
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  // 자유 텍스트 조회조건 — Report 공통 정책(§A-9b)대로 조회 버튼이 없어 ref(uncontrolled)로 소유하고
  // 필드 안의 검색 아이콘/Enter로만 수동 커밋한다(SearchOptsGroupRetrieve).
  const customerCdNmRef = useRef("");
  const resourceCdNmRef = useRef("");
  const routeCdNmRef = useRef("");
  const itemCdNmRef = useRef("");
  const dmndCdNmRef = useRef("");
  const woCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const latestReqRef = useRef(0);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrievePlanDetail(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false, enableRowGroup: true },
      // old gridView.setGroupPanel({visible:true})의 유일한 대응 — 조회조건으로 좁힌 raw 활동
      // 데이터를 사용자가 아무 컬럼이나 골라 자유롭게 그룹핑하는 것 자체가 이 화면의 핵심 기능이라,
      // 통상 Tier2(행 그룹핑·피벗)로 유보하는 규칙의 예외로 Tier1 승격(사용자 승인, 2026-08-12).
      // old는 컬럼별 그룹 제한이 전혀 없었으므로(21개 전 컬럼 자유 드래그) enableRowGroup은
      // 개별 컬럼이 아니라 defaultColDef에서 전체 컬럼에 일괄 적용한다(fp-report-screen-guide.md §B-6).
      rowGroupPanelShow: "always",
      // 2026-08-12 사용자 지시 — 그룹 패널에 컬럼을 놓으면 트리가 접힌 채 시작하지 않고
      // 항상 전부 펼친 상태로 보여준다(-1 = 전체 레벨 펼침).
      groupDefaultExpanded: -1
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old gridItems 1:1 대조 — 21개 필드, 고정 rowGroup 없음(자유 그룹핑, 위 rowGroupPanelShow 참고).
  // old 숫자 컬럼 전체(priority/qty/processTime/queueTime/waitTime/moveTime)엔 wingui-core
  // stdGridWrapper.js가 전 숫자 컬럼에 자동으로 걸어주던 groupFooter(그룹 합계)가 있었지만,
  // 2026-08-12 사용자 지시로 new는 수량(qty) 컬럼에만 aggFunc:"sum"을 건다 — 나머지 숫자 컬럼
  // (priority/processTime/queueTime/waitTime/moveTime)은 그룹 패널에 놓아도 합계가 표시되지 않는다.
  const columnDefs = useMemo(() => [
    { headerName: "FP_SO_CD", field: "dmndCd", colId: "dmndCd", width: 150 },
    { headerName: "FP_CUSTOMER_CD", field: "customerCd", colId: "customerCd", width: 150 },
    { headerName: "FP_CUSTOMER_NM", field: "customerNm", colId: "customerNm", width: 150 },
    { headerName: "FP_DUE_DT", field: "dueDate", colId: "dueDate", width: 175, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_WO_CD", field: "woCd", colId: "woCd", width: 150 },
    {
      // "우선 순위"(한글) 헤더가 정렬/메뉴 아이콘과 함께 잘리지 않는 폭으로 조정(기존 60 → 100).
      headerName: "FP_PRIORITY", field: "priority", colId: "priority", width: 100,
      type: ["numberColumn", "rightAligned"]
    },
    // old visible:false — 컬럼 자체는 유지하되 기본 숨김(엑셀 export/그룹 대상에서는 여전히 선택 가능).
    { headerName: "FP_ACTIVITY_ID", field: "activityId", colId: "activityId", width: 80, hide: true },
    { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150 },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", width: 150 },
    { headerName: "FP_WEEK", field: "startWeek", colId: "startWeek", width: 150 },
    { headerName: "FP_START_DT", field: "startTs", colId: "startTs", width: 175, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_END_DT", field: "endTs", colId: "endTs", width: 175, type: ["dateTimeColumn", "centerAligned"] },
    {
      headerName: "FP_QTY", field: "qty", colId: "qty", width: 100,
      type: ["numberColumn", "rightAligned"], aggFunc: "sum"
    },
    { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", width: 200 },
    { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", width: 200 },
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", width: 150 },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", width: 150 },
    {
      headerName: "FP_PRCS_TM_MIN", field: "processTime", colId: "processTime", width: 150,
      type: ["numberColumn", "rightAligned"]
    },
    {
      headerName: "FP_QUEUE_TM_MIN", field: "queueTime", colId: "queueTime", width: 150,
      type: ["numberColumn", "rightAligned"]
    },
    {
      headerName: "FP_WAIT_TM_MIN", field: "waitTime", colId: "waitTime", width: 150,
      type: ["numberColumn", "rightAligned"]
    },
    {
      headerName: "FP_MOVE_TM_MIN", field: "moveTime", colId: "moveTime", width: 150,
      type: ["numberColumn", "rightAligned"]
    }
    // TODO(MIG-T2): [plandetail/PlanDetail] 컬럼 자동 필터(autoFilter) 10개(dmndCd/customerCd/
    //   customerNm/woCd/itemCd/itemNm/routeCd/routeNm/resourceCd/resourceNm) 미이관.
    //   old: RealGrid `autoFilter:true` — 헤더에 값 목록 드롭다운 필터.
    //   사유: 컬럼 필터는 saas-fp/docs/fp-frontend-migration/CLAUDE.md Tier 규칙상 전 화면 공통 Tier2(1차 유보) 대상
    //         (docs/03-GRID-realgrid-to-aggrid.md autoFilter 매핑 참고, route/resource/stock/wip 등과 동일).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const retrievePlanDetail = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      customerCdNm: customerCdNmRef.current,
      resourceCdNm: resourceCdNmRef.current,
      routeCdNm: routeCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      dmndCdNm: dmndCdNmRef.current,
      woCdNm: woCdNmRef.current,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
      // TODO(MIG-T2): [plandetail/PlanDetail] 조회조건 stageCdNm/inventoryCdNm 입력 필드 미노출.
      //   old: SearchOptsGroup에 스테이지/인벤토리 자유 텍스트 검색 필드 존재, 값 입력 시 params로 전송.
      //   사유: 2026-08-12 사용자 지시로 프론트 노출만 제거(백엔드 PlanDetailQueryRepository는
      //         두 파라미터를 여전히 contains 조건으로 소비하므로 언제든 필드만 다시 추가하면 됨).
      // TODO(MIG-T2): [plandetail/PlanDetail] localeDateFormat(사용자별 날짜 포맷) 미전송.
      //   old: localeStoreApi.getState().dateFormat을 실어 보내 PlanDetailService가 startTs/endTs의
      //        날짜 부분을 그 포맷으로 재정규화
      //   사유: 안 보내면 백엔드 기본 포맷으로 처리되고, 그리드 표시는 어차피
      //        dateUtils.formatDateTime으로 다시 포맷하므로 화면상 차이 없음(dispatchlist/DispatchList와 동일 판정)
    };

    gridRef.current?.api?.setGridOption("loading", true);

    fpPlanDetailService.getPlanDetail(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = response.data?.data ?? [];
        setRowData(rows);

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const statusBar = useMemo(() => buildFlatReportStatusBar(), []);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod — PlanResult.js/DispatchList.js와 동일 원리).
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRowData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b).
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        {/* old useBucket={false} — 이 화면은 버킷 개념이 없는 activity 목록이라 버킷 Select를 숨긴다. */}
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          useBucket={false}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{
            customerCdNm: customerCdNmRef, resourceCdNm: resourceCdNmRef,
            routeCdNm: routeCdNmRef, itemCdNm: itemCdNmRef,
            dmndCdNm: dmndCdNmRef, woCdNm: woCdNmRef
          }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrievePlanDetail}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <Box id="fp-plan-detail-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, mt: 1, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          {...defaultGridMemo}
          statusBar={statusBar}
          onGridReady={onGridReady}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PlanDetail);
